import type { ModifiedDutyEmployee } from '../../interfaces/ModifiedDutyInterface';

/**
 * Pure helpers for the v2 Modified Duty page. No React, no clock reads — every
 * date-dependent function takes `today` as a YYYY-MM-DD string so tests can pin it.
 *
 * Dates (FromDate / ToDate) are SQL `date` columns serialized as UTC-midnight ISO
 * strings ("2026-08-31T00:00:00.000Z"). They are handled as YYYY-MM-DD strings (the
 * ISO prefix) and only ever turned into Dates via Date.UTC — never local getters, or
 * a Pacific browser would show the previous day.
 *
 * Rules from docs/featureBriefs/design_handoff_modified_duty/README.md:
 *  - Active = toDate null OR toDate >= today; Inactive = toDate < today.
 *  - Open = active with no end date; Ends soon = 0 <= toDate - today <= 7;
 *    Upcoming = fromDate > today (still Active).
 *  - Days: running rows count from..today ("so far"), ended rows count from..to,
 *    upcoming rows have none. Inclusive.
 */

export type Scope = 'Active' | 'Inactive' | 'All';
export const SCOPES: Scope[] = ['Active', 'Inactive', 'All'];

export const ENDS_SOON_DAYS = 7;
export const NOTE_MAX = 500;
/** Sentinel for "employee has no supervisor" in the Superintendent filter. */
export const NO_SUPER = '(none)';

const DAY_MS = 86_400_000;

/* ------------------------------------------------------------------ dates */

/** YYYY-MM-DD from a SQL-date ISO string ("2026-08-31T00:00:00.000Z" or "2026-08-31"), else null. */
export function ymd(s: string | null | undefined): string | null {
  if (!s) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(s));
  return m ? m[1] : null;
}

/** The viewer's local calendar date as YYYY-MM-DD. */
export function localTodayYMD(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function utcMs(s: string): number {
  const [y, m, d] = s.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Whole days from a to b (b − a). Both YYYY-MM-DD. */
export function dayDiff(a: string, b: string): number {
  return Math.round((utcMs(b) - utcMs(a)) / DAY_MS);
}

/** MM/DD/YYYY, or '' for null. */
export function fmtDate(s: string | null | undefined): string {
  const v = ymd(s);
  if (!v) return '';
  const [y, m, d] = v.split('-');
  return `${m}/${d}/${y}`;
}

/** MM/DD */
export function fmtShort(s: string | null | undefined): string {
  return fmtDate(s).slice(0, 5);
}

export function money(n: number | string): string {
  return '$' + Number(n).toFixed(2);
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/* ------------------------------------------------------------------ rows */

export interface MdRow {
  rec: ModifiedDutyEmployee;
  id: number;
  name: string;
  /** Business employee ID (altEmployeeID), as shown on screen. */
  empID: string;
  sup: string | null;
  from: string;
  to: string | null;
  rate: number;
  notes: string;
  inactive: boolean;
  upcoming: boolean;
  open: boolean;
  endsSoon: boolean;
  /** Days until toDate (negative when ended), null when open. */
  left: number | null;
  /** Inclusive day count shown in the Days column; null for upcoming rows. */
  days: number | null;
  status: 'Active' | 'Inactive' | 'Open';
}

export function employeeName(rec: Pick<ModifiedDutyEmployee, 'employee'>): string {
  const e = rec.employee;
  if (!e) return '';
  return [e.firstName, e.lastName].filter(Boolean).join(' ').trim();
}

export function deriveRow(rec: ModifiedDutyEmployee, today: string): MdRow {
  const from = ymd(rec.fromDate) ?? today;
  const to = ymd(rec.toDate);
  const inactive = to != null && to < today;
  const upcoming = !inactive && from > today;
  const open = !inactive && to == null;
  const left = to != null ? dayDiff(today, to) : null;
  const endsSoon = !inactive && left != null && left <= ENDS_SOON_DAYS;
  let days: number | null;
  if (inactive) days = dayDiff(from, to as string) + 1;
  else if (upcoming) days = null;
  else days = dayDiff(from, today) + 1;
  const sup = rec.employee?.supervisorName?.trim() || null;
  return {
    rec,
    id: rec.modifiedDutyEmployeesID,
    name: employeeName(rec),
    empID: String(rec.employee?.altEmployeeID ?? rec.employeeID ?? ''),
    sup,
    from,
    to,
    rate: Number(rec.hourlyRate) || 0,
    notes: rec.notes ?? '',
    inactive,
    upcoming,
    open,
    endsSoon,
    left,
    days,
    status: inactive ? 'Inactive' : open ? 'Open' : 'Active',
  };
}

export function inScope(row: Pick<MdRow, 'inactive'>, scope: Scope): boolean {
  if (scope === 'All') return true;
  return scope === 'Active' ? !row.inactive : row.inactive;
}

/* ------------------------------------------------------------------ filters */

export interface ColumnFilters {
  emp: string;
  empId: string;
  sup: string[];
  fromA: string;
  fromB: string;
  toA: string;
  toB: string;
  toOpen: boolean;
  rateMin: string;
  rateMax: string;
  notes: string;
}

export type FilterCol = 'emp' | 'sup' | 'from' | 'to' | 'rate' | 'notes';
/** A removable chip key — Employee ID has its own chip though it lives in the Employee popover. */
export type ChipKey = FilterCol | 'empId';

export const emptyFilters = (): ColumnFilters => ({
  emp: '', empId: '', sup: [], fromA: '', fromB: '', toA: '', toB: '', toOpen: false,
  rateMin: '', rateMax: '', notes: '',
});

const low = (s: string | null | undefined) => String(s ?? '').toLowerCase();

export function matchesSearch(row: MdRow, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [row.name, row.empID, row.sup ?? '', row.notes].some((v) => low(v).includes(t));
}

export function matchesFilters(row: MdRow, f: ColumnFilters): boolean {
  const emp = f.emp.trim();
  if (emp && !(low(row.name).includes(emp.toLowerCase()) || low(row.empID).includes(emp.toLowerCase()))) return false;
  const empId = f.empId.trim();
  if (empId && !low(row.empID).includes(empId.toLowerCase())) return false;
  if (f.sup.length && !f.sup.includes(row.sup ?? NO_SUPER)) return false;
  if (f.fromA && row.from < f.fromA) return false;
  if (f.fromB && row.from > f.fromB) return false;
  const toRange = !!(f.toA || f.toB);
  if (toRange || f.toOpen) {
    const t = row.to;
    const inRange = !!t && (!f.toA || t >= f.toA) && (!f.toB || t <= f.toB);
    const ok = (f.toOpen && !t) || (toRange && inRange);
    if (!ok) return false;
  }
  const min = parseFloat(f.rateMin);
  if (f.rateMin !== '' && !Number.isNaN(min) && row.rate < min) return false;
  const max = parseFloat(f.rateMax);
  if (f.rateMax !== '' && !Number.isNaN(max) && row.rate > max) return false;
  const notes = f.notes.trim();
  if (notes && !low(row.notes).includes(notes.toLowerCase())) return false;
  return true;
}

export function isColumnFiltered(f: ColumnFilters, col: FilterCol): boolean {
  switch (col) {
    case 'emp': return !!(f.emp.trim() || f.empId.trim());
    case 'sup': return f.sup.length > 0;
    case 'from': return !!(f.fromA || f.fromB);
    case 'to': return !!(f.toA || f.toB || f.toOpen);
    case 'rate': return f.rateMin !== '' || f.rateMax !== '';
    case 'notes': return !!f.notes.trim();
  }
}

/** Clear one chip / column. Clearing 'emp' (the column) clears both Employee inputs. */
export function clearFilter(f: ColumnFilters, key: ChipKey): ColumnFilters {
  switch (key) {
    case 'emp': return { ...f, emp: '', empId: '' };
    case 'empId': return { ...f, empId: '' };
    case 'sup': return { ...f, sup: [] };
    case 'from': return { ...f, fromA: '', fromB: '' };
    case 'to': return { ...f, toA: '', toB: '', toOpen: false };
    case 'rate': return { ...f, rateMin: '', rateMax: '' };
    case 'notes': return { ...f, notes: '' };
  }
}

export interface Chip { key: ChipKey; label: string; value: string; title?: string }

export function filterChips(f: ColumnFilters): Chip[] {
  const out: Chip[] = [];
  const q = (s: string) => `“${s.trim()}”`;
  const rng = (a: string, b: string) => (a && b ? `${fmtDate(a)} – ${fmtDate(b)}` : a ? `on/after ${fmtDate(a)}` : `on/before ${fmtDate(b)}`);
  if (f.emp.trim()) out.push({ key: 'emp', label: 'Employee', value: q(f.emp) });
  if (f.empId.trim()) out.push({ key: 'empId', label: 'Employee ID', value: q(f.empId) });
  if (f.sup.length) {
    out.push({
      key: 'sup',
      label: 'Superintendent',
      value: f.sup[0] + (f.sup.length > 1 ? ` +${f.sup.length - 1}` : ''),
      title: f.sup.join(', '),
    });
  }
  if (f.fromA || f.fromB) out.push({ key: 'from', label: 'From', value: rng(f.fromA, f.fromB) });
  if (f.toA || f.toB || f.toOpen) {
    out.push({
      key: 'to',
      label: 'To',
      value: [f.toOpen ? 'Open' : '', f.toA || f.toB ? rng(f.toA, f.toB) : ''].filter(Boolean).join(' or '),
    });
  }
  if (f.rateMin !== '' || f.rateMax !== '') {
    const v = f.rateMin !== '' && f.rateMax !== ''
      ? `${money(f.rateMin)} – ${money(f.rateMax)}`
      : f.rateMin !== '' ? `≥ ${money(f.rateMin)}` : `≤ ${money(f.rateMax)}`;
    out.push({ key: 'rate', label: 'Rate', value: v });
  }
  if (f.notes.trim()) out.push({ key: 'notes', label: 'Notes', value: q(f.notes) });
  return out;
}

/* ------------------------------------------------------------------ sort */

export type SortKey = 'emp' | 'sup' | 'from' | 'to' | 'days' | 'rate' | 'notes';
export interface SortState { key: SortKey; dir: 'asc' | 'desc' }

export const DEFAULT_SORT: SortState = { key: 'from', dir: 'desc' };

export const SORT_LABEL: Record<SortKey, string> = {
  emp: 'Employee', sup: 'Superintendent', from: 'From', to: 'To', days: 'Days', rate: 'Hourly Rate', notes: 'Notes',
};

/** Clicking the active column flips it; a new column starts dates/numbers desc, text asc. */
export function nextSort(cur: SortState, key: SortKey): SortState {
  if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: key === 'from' || key === 'to' || key === 'days' || key === 'rate' ? 'desc' : 'asc' };
}

const cmpText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Sort a copy. Blank superintendent / notes always sort last (either direction);
 * an open To sorts as the latest date. Ties fall back to From desc, then id desc.
 */
export function sortRows(rows: MdRow[], sort: SortState): MdRow[] {
  const d = sort.dir === 'asc' ? 1 : -1;
  const primary = (a: MdRow, b: MdRow): number => {
    switch (sort.key) {
      case 'emp': return cmpText(a.name, b.name) * d;
      case 'sup':
      case 'notes': {
        const A = sort.key === 'sup' ? a.sup ?? '' : a.notes.trim();
        const B = sort.key === 'sup' ? b.sup ?? '' : b.notes.trim();
        if (!A && !B) return 0;
        if (!A) return 1;
        if (!B) return -1;
        return cmpText(A, B) * d;
      }
      case 'from': return (a.from < b.from ? -1 : a.from > b.from ? 1 : 0) * d;
      case 'to': {
        const A = a.to ?? '9999-12-31';
        const B = b.to ?? '9999-12-31';
        return (A < B ? -1 : A > B ? 1 : 0) * d;
      }
      case 'days': return ((a.days ?? -1) - (b.days ?? -1)) * d;
      case 'rate': return (a.rate - b.rate) * d;
    }
  };
  return rows.slice().sort((a, b) =>
    primary(a, b)
    || (a.from < b.from ? 1 : a.from > b.from ? -1 : 0)
    || b.id - a.id);
}

/* ------------------------------------------------------------------ counts */

export function scopeCounts(rows: Pick<MdRow, 'inactive'>[]): Record<Scope, number> {
  const c: Record<Scope, number> = { Active: 0, Inactive: 0, All: rows.length };
  for (const r of rows) c[r.inactive ? 'Inactive' : 'Active']++;
  return c;
}

/** Distinct supervisor names across every record (sorted), then (none) last. */
export function supervisorOptions(rows: MdRow[]): string[] {
  const names = Array.from(new Set(rows.map((r) => r.sup).filter((s): s is string => !!s)));
  names.sort(cmpText);
  names.push(NO_SUPER);
  return names;
}

export function supervisorCounts(rows: MdRow[]): Record<string, number> {
  const c: Record<string, number> = {};
  for (const r of rows) {
    const k = r.sup ?? NO_SUPER;
    c[k] = (c[k] ?? 0) + 1;
  }
  return c;
}

/* ------------------------------------------------------------------ modal */

export interface MdForm {
  mode: 'add' | 'edit';
  employeeID: number | null;
  from: string;
  to: string;
  rate: string;
  notes: string;
}

export type FormErrors = Partial<Record<'emp' | 'from' | 'to' | 'rate', string>>;

export function validateForm(m: MdForm): FormErrors {
  const e: FormErrors = {};
  if (m.mode === 'add' && m.employeeID == null) e.emp = 'Choose an employee.';
  if (!m.from) e.from = 'Enter a start date.';
  if (m.from && m.to && m.to < m.from) e.to = "End date can't be before the start date.";
  const n = parseFloat(m.rate);
  if (m.rate.trim() === '' || Number.isNaN(n)) e.rate = 'Enter an hourly rate.';
  else if (n <= 0) e.rate = 'Rate must be greater than $0.00.';
  return e;
}

/** The employee's open-ended record (no end date and not ended), if any. */
export function openRecordFor(records: ModifiedDutyEmployee[], employeeID: number | null): ModifiedDutyEmployee | null {
  if (employeeID == null) return null;
  return records.find((r) => r.employeeID === employeeID && !ymd(r.toDate)) ?? null;
}

/** The API's SQL `date` params take a UTC-midnight ISO string (same as the v1 modal sent). */
export function toApiDate(s: string): string {
  return s ? `${s}T00:00:00.000Z` : '';
}
